import { parseDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";
import { round } from "lodash-es";
import {
  CalendarIcon,
  CameraIcon,
  CircleCheckIcon,
  EditIcon,
  TrophyIcon,
  UserIcon,
} from "lucide-react";
import { Suspense } from "react";
import { Button as AriaButton, DialogTrigger } from "react-aria-components";
import { titleCase } from "title-case";
import z from "zod";

import { isAdminRole, useViewer, useViewerHasPermission } from "@/auth/shared";
import { button } from "@/components/base/button";
import { ComboBox, ComboBoxItem } from "@/components/base/combo-box";
import { Link } from "@/components/base/link";
import { Pagination } from "@/components/base/pagination";
import { Popover } from "@/components/base/popover";
import { title } from "@/components/base/primitives";
import { Select } from "@/components/base/select";
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@/components/base/table";
import { tagStyles } from "@/components/base/tag-group";
import { ImpersonateButton } from "@/components/impersonator/impersonate-button";
import { ProfileName } from "@/components/profiles/name";
import { ProfilePhoto } from "@/components/profiles/photo";
import { TeamNames } from "@/components/teams/names";
import { useDivisionFilterOptions } from "@/data/divisions";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useVenueFilterOptions } from "@/hooks/venues";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getDefaultTimeZone } from "@/lib/dates";
import { getJuniorsBaseYear } from "@/lib/juniors";
import { isNotNullOrUndefined } from "@/utils/types";

const juniorRatingColors: Record<string, { ring: string; badge: string }> = {
  aaa: {
    ring: "ring-6 ring-yellow-500",
    badge: "bg-yellow-500 text-black",
  },
  aa: {
    ring: "ring-6 ring-(--navy)",
    badge: "bg-(--navy) text-white",
  },
  a: {
    ring: "ring-6 ring-sky-400",
    badge: "bg-sky-400 text-white",
  },
  b: {
    ring: "ring-6 ring-red-600",
    badge: "bg-red-600 text-white",
  },
};

const profileSearchSchema = z.object({
  page: z.number().min(1).default(1),
  divisions: z.array(z.number()).default([]),
  venues: z.array(z.number()).default([]),
});

export const Route = createFileRoute("/profile/$profileId/")({
  validateSearch: profileSearchSchema,
  loader: async ({ params: { profileId }, context: { queryClient, trpc } }) => {
    const result = await queryClient.ensureQueryData(
      trpc.profiles.getOverview.queryOptions({
        id: Number.parseInt(profileId, 10),
      }),
    );

    return result;
  },
  head: () => ({
    meta: [{ title: "Player Profile" }],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { profileId } = Route.useParams();
  const { page, venues, divisions } = Route.useSearch();
  const pageSize = 25;
  const trpc = useTRPC();

  const { data: profile } = useSuspenseQuery(
    trpc.profiles.getOverview.queryOptions({
      id: Number.parseInt(profileId, 10),
    }),
  );

  const dateFormatter = useDateFormatter({
    dateStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  const { data: resultData } = useSuspenseQuery({
    ...trpc.profiles.getResults.queryOptions({
      id: Number.parseInt(profileId, 10),
      venues,
      divisions,
      paging: {
        page,
        size: pageSize,
      },
    }),
    select: (data) => ({
      ...data,
      data: data.data.map(({ date, ...rest }) => ({
        date: dateFormatter.format(
          parseDate(date).toDate(getDefaultTimeZone()),
        ),
        ...rest,
      })),
    }),
  });

  const ratingSinceFormatter = useDateFormatter({
    month: "short",
    year: "numeric",
    timeZone: getDefaultTimeZone(),
  });
  const ratingSinceLabel = profile.currentRatingSince
    ? ratingSinceFormatter.format(
        parseDate(profile.currentRatingSince).toDate(getDefaultTimeZone()),
      )
    : null;

  const accolades: Array<{
    label: string;
    value: string | number | null | undefined;
    subtext?: string | null;
  }> = [
    {
      label: "Rating",
      value: profile?.level?.abbreviated ?? profile?.level?.name,
      subtext: ratingSinceLabel ? `since ${ratingSinceLabel}` : null,
    },
    {
      label: "Rank",
      value: profile?.rank,
    },
    {
      label: "Points",
      value: round(profile?.ratedPoints),
    },
    {
      label: "Junior Points",
      value: profile?.juniorsPoints ? round(profile?.juniorsPoints) : null,
    },
  ].filter(({ value }) => isNotNullOrUndefined(value));

  const venueOptions = useVenueFilterOptions(true);
  const divisionOptions = useDivisionFilterOptions(true);

  const info = [
    {
      label: "Bio",
      className: "col-span-full",
      value: profile.bio,
    },
    {
      label: "Dominant Arm",
      value: profile.dominantArm,
      transform: (v: string) => titleCase(v),
    },
    {
      label: "Preferred Role",
      value: profile.preferredRole,
      transform: (v: string) => titleCase(v),
    },
    {
      label: "Height",
      value: profile.heightFeet
        ? `${profile.heightFeet}'${profile.heightInches ? `${profile.heightInches}"` : ""}`
        : null,
    },
    {
      label: "Preferred Side",
      value: profile.preferredSide,
      transform: (v: string) => titleCase(v),
    },
    {
      label: "Club",
      value: profile.club,
    },
    {
      label: "College Team",
      value: profile.collegeTeam
        ? (() => {
            const details = [
              profile.collegeCommitmentLevel &&
              profile.collegeCommitmentLevel !== "uncommitted"
                ? titleCase(profile.collegeCommitmentLevel.replace(/_/g, " "))
                : null,
              profile.collegeTeamYearsParticipated
                ? `${profile.collegeTeamYearsParticipated} year(s)`
                : null,
            ].filter(Boolean);
            return details.length
              ? `${profile.collegeTeam} (${details.join(", ")})`
              : profile.collegeTeam;
          })()
        : null,
    },
  ].filter(({ value }) => value !== null);

  const juniorRating =
    profile.level?.name &&
    profile.highSchoolGraduationYear != null &&
    profile.highSchoolGraduationYear >= getJuniorsBaseYear()
      ? juniorRatingColors[profile.level.name]
      : null;

  const viewer = useViewer();

  const isViewer = viewer && viewer.id === profile.userId;

  const canEdit = useViewerHasPermission({
    user: ["update"],
  });

  return (
    <DefaultLayout
      classNames={{
        content: "pt-18 w-full relative bg-white",
      }}
    >
      <div className="absolute top-6 right-6 flex flex-row gap-2">
        <ImpersonateButton userId={profile?.userId} />
        {isAdminRole(viewer?.role) && profile.userId && (
          <Link
            className={button({ color: "secondary" })}
            to="/admin/user/$userId"
            params={{
              userId: profile.userId,
            }}
          >
            <UserIcon size={14} className="mr -ml" />
            Account
          </Link>
        )}
        {(isViewer || canEdit) && (
          <Link
            className={button({ color: "secondary" })}
            to="/profile/$profileId/edit"
            params={{
              profileId: profile.id.toString(),
            }}
          >
            <EditIcon size={14} className="mr -ml" />
            Edit
          </Link>
        )}
      </div>

      <Suspense>
        <div className="mx-auto flex max-w-5xl flex-col items-center px-4 pb-18 md:flex-row md:space-x-8">
          <div className={clsx("relative rounded-full", juniorRating?.ring)}>
            <ProfilePhoto
              {...profile}
              profileId={profile.id}
              canUpdate={Boolean(isViewer || canEdit)}
              className="mx-auto h-48 min-h-48 w-48 min-w-48"
            />
            {juniorRating && profile.level && (
              <span
                className={clsx(
                  "absolute bottom-1 left-1 rounded-full px-3 py-1 text-sm font-bold uppercase shadow",
                  juniorRating.badge,
                )}
              >
                {profile.level.abbreviated ?? profile.level.name}
              </span>
            )}
          </div>

          <div className="flex w-full flex-col items-start space-y-4 sm:items-stretch">
            <div className="flex w-full flex-col items-center space-y-3 py-2 md:flex-row md:space-y-0 md:space-x-6">
              <h1 className={title({ size: "sm", class: "text-center" })}>
                <ProfileName {...profile} link={false} />
              </h1>

              {profile.activeMembership ? (
                <span className={tagStyles({ color: "blue" })}>
                  <CircleCheckIcon size={16} /> <span>Active Member</span>
                </span>
              ) : (
                <Link
                  variant="alt"
                  to="/account/registrations"
                  search={{
                    memberships: [
                      { profileId: profile.id, tshirtSize: undefined },
                    ],
                  }}
                  className={tagStyles({ color: "red" })}
                >
                  Purchase Membership
                </Link>
              )}

              {profile.earnedBids.length > 0 && (
                <DialogTrigger>
                  <AriaButton className={tagStyles({ color: "yellow" })}>
                    <TrophyIcon size={16} /> <span>Bid Earned</span>
                  </AriaButton>
                  <Popover showArrow placement="bottom">
                    <div className="p-3">
                      <p className="mb-1 text-sm font-semibold">
                        Bid earned at:
                      </p>
                      <ul className="text-sm">
                        {profile.earnedBids.map((bid) => (
                          <li key={`${bid.tournamentId}-${bid.divisionId}`}>
                            <Link
                              to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                              params={{
                                tournamentId: bid.tournamentId.toString(),
                                divisionId: bid.divisionId.toString(),
                                tab: "playoffs",
                              }}
                            >
                              {bid.tournament}
                            </Link>
                            <div className="text-muted-foreground text-xs">
                              {bid.division}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Popover>
                </DialogTrigger>
              )}
            </div>

            <div className="mx-auto grid max-w-fit grid-cols-2 gap-4 sm:flex sm:w-full sm:flex-row sm:justify-start sm:gap-12 md:mx-0">
              {accolades.map(({ label, value, subtext }) => (
                <div key={label}>
                  <span className="font-semibold">{label}</span>
                  <div className="text-5xl font-semibold uppercase">
                    {value}
                  </div>
                  {subtext && (
                    <div className="text-sm text-gray-500">{subtext}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        {info.length > 0 && (
          <div className="bg-content-background-alt">
            <div className="mx-auto grid max-w-5xl grid-cols-1 gap-4 gap-y-8 px-4 py-16 text-center sm:grid-cols-9 sm:text-left">
              {info.map(
                ({ label, className = "col-span-3", value, transform }) => (
                  <div className={className} key={label}>
                    <span className="mb-2 font-semibold">{label}</span>
                    <p>{transform ? transform(value as string) : value}</p>
                  </div>
                ),
              )}
            </div>
          </div>
        )}
      </Suspense>

      <Suspense>
        <UpcomingRegistrations profileId={Number.parseInt(profileId, 10)} />
      </Suspense>

      <Suspense>
        <div className="bg-content-background flex w-full flex-col space-y-12 py-16">
          <div className="mx-auto flex w-full max-w-5xl flex-col space-y-12 px-4">
            <h2 className={title()}>Results</h2>

            <div className="flex flex-col space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row">
                <Suspense>
                  <ComboBox
                    selectedKeys={venues}
                    value={venues}
                    multi={true}
                    items={venueOptions}
                    placeholder="All Beaches"
                    className="w-full sm:max-w-xs"
                  >
                    {({ value, display, link }) => (
                      <ComboBoxItem id={value} link={link}>
                        {display}
                      </ComboBoxItem>
                    )}
                  </ComboBox>
                </Suspense>

                <Suspense>
                  <Select
                    defaultValue={divisions}
                    value={divisions}
                    selectionMode="multiple"
                    options={divisionOptions}
                    placeholder="All Divisions"
                    className="bg-white"
                    containerClassName="w-full sm:max-w-xs"
                  />
                </Suspense>
              </div>

              <Table aria-label="Player's tournament results">
                <TableHeader className="bg-navbar-background">
                  <TableColumn id="date" allowsSorting minWidth={100}>
                    Date
                  </TableColumn>
                  <TableColumn id="tournament" isRowHeader minWidth={100}>
                    Event
                  </TableColumn>
                  <TableColumn id="venue" isRowHeader minWidth={90}>
                    Beach
                  </TableColumn>
                  <TableColumn id="division" isRowHeader minWidth={90}>
                    Division
                  </TableColumn>
                  <TableColumn id="players" isRowHeader minWidth={90}>
                    Players
                  </TableColumn>
                  <TableColumn id="finish" isRowHeader minWidth={90}>
                    Finish
                  </TableColumn>
                  <TableColumn id="rating" isRowHeader minWidth={90}>
                    Rating
                  </TableColumn>
                  <TableColumn id="points" isRowHeader minWidth={90}>
                    Points
                  </TableColumn>
                </TableHeader>

                <TableBody
                  items={resultData.data}
                  renderEmptyState={() => (
                    <div className="p-2 text-center">No results yet.</div>
                  )}
                >
                  {({
                    id,
                    tournamentId,
                    tournamentDivisionId,
                    date,
                    event,
                    venue,
                    division,
                    players,
                    finish,
                    rating,
                    points,
                    hasGalleryPhotos,
                  }) => {
                    return (
                      <TableRow key={id}>
                        <TableCell>
                          <Link
                            className="no-underline hover:underline"
                            to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                            params={{
                              tournamentId: tournamentId.toString(),
                              divisionId: tournamentDivisionId.toString(),
                            }}
                          >
                            {date}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1">
                            <Link
                              className="no-underline hover:underline"
                              to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                              params={{
                                tournamentId: tournamentId.toString(),
                                divisionId: tournamentDivisionId.toString(),
                              }}
                            >
                              {event}
                            </Link>
                            {hasGalleryPhotos && (
                              <Link
                                to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                                params={{
                                  tournamentId: tournamentId.toString(),
                                  divisionId: tournamentDivisionId.toString(),
                                  tab: "gallery",
                                }}
                                className="text-gray-400 hover:text-blue-600"
                              >
                                <CameraIcon size={14} />
                              </Link>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>{venue}</TableCell>
                        <TableCell>{division}</TableCell>
                        <TableCell>
                          <TeamNames players={players} />
                        </TableCell>
                        <TableCell>{finish}</TableCell>
                        <TableCell>{rating}</TableCell>
                        <TableCell>{points}</TableCell>
                      </TableRow>
                    );
                  }}
                </TableBody>
              </Table>

              <Pagination
                to={Route.fullPath}
                page={page}
                pageSize={pageSize}
                pageInfo={resultData.pageInfo}
              />
            </div>
          </div>
        </div>
      </Suspense>
    </DefaultLayout>
  );
}

function UpcomingRegistrations({ profileId }: { profileId: number }) {
  const trpc = useTRPC();
  const dateFormatter = useDateFormatter({
    dateStyle: "medium",
    timeZone: getDefaultTimeZone(),
  });

  const { data } = useQuery(
    trpc.profiles.getRegistrations.queryOptions({ profileId }),
  );

  // Don't render if not authorized or no registrations
  if (!data?.authorized || data.registrations.length === 0) {
    return null;
  }

  return (
    <div className="bg-content-background-alt">
      <div className="mx-auto flex max-w-5xl flex-col space-y-6 px-4 py-16">
        <h2 className={title()}>Upcoming Tournaments</h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.registrations.map((reg) => (
            <Link
              key={reg.id}
              to="/tournaments/$tournamentId/$divisionId/{-$tab}"
              params={{
                tournamentId: reg.tournament.id.toString(),
                divisionId: reg.division.id.toString(),
              }}
              className="rounded-lg bg-white p-4 no-underline shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <div className="bg-primary/10 rounded-lg p-2">
                  <CalendarIcon className="text-primary h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-foreground truncate font-semibold">
                    {reg.tournament.name ?? `${reg.venue.name} Tournament`}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {dateFormatter.format(
                      parseDate(reg.tournament.date).toDate(
                        getDefaultTimeZone(),
                      ),
                    )}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {reg.venue.name}, {reg.venue.city}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span
                      className={tagStyles({
                        color:
                          reg.status === "confirmed"
                            ? "green"
                            : reg.status === "waitlisted"
                              ? "blue"
                              : "yellow",
                      })}
                    >
                      {titleCase(reg.status)}
                      {reg.waitlistPosition != null &&
                        ` #${reg.waitlistPosition}`}
                    </span>
                    <span className={tagStyles({ color: "gray" })}>
                      {getTournamentDivisionDisplay(reg.division)}
                    </span>
                  </div>
                  {reg.teammates.length > 0 && (
                    <div className="text-muted-foreground mt-2 text-sm">
                      <TeamNames
                        players={reg.teammates.map((tm) => ({
                          profile: {
                            id: tm.profileId,
                            firstName: tm.firstName,
                            preferredName: tm.preferredName,
                            lastName: tm.lastName,
                          },
                        }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
